import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import usePreview from './usePreview';

// Implementation based on the following feature:
// https://nextjs.org/docs/pages/building-your-application/configuring/preview-mode

interface SwitchProps {
  isPreview: boolean;
  isLoading: boolean;
  handlePreview: () => void;
}

const Switch = ({ isPreview, isLoading, handlePreview }: SwitchProps) => {
  // Uses local state to toggle the button until the page is reloaded
  const [enabled, setEnabled] = useState(isPreview);

  useEffect(() => {
    setEnabled(isPreview);
  }, [isPreview]);

  return (
    <button
      onClick={() => {
        setEnabled((enabled) => !enabled);
        handlePreview();
      }}
      className={classNames(
        'text-xs font-bold relative flex items-center w-16 h-8 rounded-full transition-all duration-300',
        enabled ? 'bg-blue-deep' : 'border-2 border-blue-deep bg-white',
        isLoading && 'opacity-50 cursor-not-allowed',
      )}
      disabled={isLoading}
    >
      <span
        className={classNames(
          'absolute inset-y-0 left-1 flex items-center transition-transform duration-300',
          enabled ? 'translate-x-2 text-white' : 'translate-x-7 text-blue-deep',
        )}
      >
        {enabled ? 'ON' : 'OFF'}
      </span>
      <span
        className={classNames(
          'absolute right-1  rounded-full transition-transform duration-300',
          enabled
            ? 'w-6 h-6 translate-x-0 bg-white'
            : 'w-4 h-4 -translate-x-8 border-2 bg-blue-deep border-blue-deep',
        )}
      ></span>
    </button>
  );
};

const PreviewControls = () => {
  const { environment, isPreview, handlePreview, isLoading } = usePreview();
  return (
    <div className="flex items-center justify-between mb-6 gap-10">
      <div className="flex flex-col lg:max-w-32">
        <h4 className="text-base font-bold">Preview Mode</h4>
        {isPreview && <small className="leading-4">{environment}</small>}
      </div>

      <div className="flex items-center relative">
        {isLoading && (
          <div className="animate-spin h-4 w-4 border-2 border-charcoal rounded-full border-t-transparent absolute -left-6" />
        )}
        <Switch
          isPreview={isPreview}
          isLoading={isLoading}
          handlePreview={handlePreview}
        />
      </div>
    </div>
  );
};

export default React.memo(PreviewControls);
